import React, { useState, useEffect } from 'react';
import { Search, Users, UserPlus, Shield, User, Target, Send } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import { useUser } from '../../contexts/UserContext';
import logger from '../../utils/logger';

const DiscoverView = ({ onSendFriendRequest, onSendTeamRequest, pendingRequests = new Set() }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('users'); // 'users' or 'teams'
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [suggestedPlayers, setSuggestedPlayers] = useState([]);
    const [featuredTeams, setFeaturedTeams] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(true);
    const { user } = useUser();

    // Load suggested players and featured teams on mount
    useEffect(() => {
        if (!user?.uid) return;

        let isMounted = true;

        const loadSuggestions = async () => {
            setLoadingSuggestions(true);
            try {
                // Fetch recent users from Users collection (limit 10)
                const usersQuery = query(
                    collection(db, FIRESTORE_PATHS.USERS),
                    limit(15)
                );
                const usersSnapshot = await getDocs(usersQuery);
                if (isMounted) {
                    const players = usersSnapshot.docs
                        .filter(d => d.id !== user.uid)
                        .slice(0, 10)
                        .map(d => ({ id: d.id, ...d.data(), type: 'user' }));
                    setSuggestedPlayers(players);
                }

                // Fetch featured teams (limit 5)
                const teamsQuery = query(
                    collection(db, FIRESTORE_PATHS.TEAMS),
                    limit(5)
                );
                const teamsSnapshot = await getDocs(teamsQuery);
                if (isMounted) {
                    setFeaturedTeams(teamsSnapshot.docs.map(d => ({ id: d.id, ...d.data(), type: 'team' })));
                }
            } catch (err) {
                logger.error('Discover', 'Error loading suggestions:', err);
            }
            if (isMounted) setLoadingSuggestions(false);
        };

        loadSuggestions();
        return () => { isMounted = false; };
    }, [user]);

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setLoading(true);
        setResults([]);

        try {
            if (searchType === 'users') {
                // Search users by displayName (prefix match on Users collection)
                const q = query(
                    collection(db, FIRESTORE_PATHS.USERS),
                    where('displayName', '>=', searchQuery),
                    where('displayName', '<=', searchQuery + '\uf8ff'),
                    limit(20)
                );
                const snapshot = await getDocs(q);
                setResults(snapshot.docs.filter(d => d.id !== user.uid).map(d => ({ id: d.id, ...d.data(), type: 'user' })));
            } else {
                // Search teams by name
                const q = query(
                    collection(db, FIRESTORE_PATHS.TEAMS),
                    where('meta.name', '>=', searchQuery),
                    where('meta.name', '<=', searchQuery + '\uf8ff'),
                    limit(20)
                );
                const snapshot = await getDocs(q);
                setResults(snapshot.docs.map(d => ({ id: d.id, ...d.data(), type: 'team' })));
            }
        } catch (err) {
            logger.error('Discover', 'Search error:', err);
        }
        setLoading(false);
    };

    // Render a player/team card
    const renderCard = (item) => {
        const isPending = pendingRequests.has(item.id);
        const isUser = item.type === 'user';

        return (
            <div key={item.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700 flex items-center gap-4 hover:border-gray-600 transition">
                {/* Avatar / Crest */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${isUser ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-gradient-to-br from-orange-500 to-red-600'}`}>
                    {isUser ? (item.displayName?.charAt(0) || '?') : (item.meta?.name?.charAt(0) || 'T')}
                </div>
                {/* Info */}
                <div className="flex-1">
                    <h3 className="text-white font-bold">{isUser ? item.displayName : item.meta?.name}</h3>
                    <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                        {isUser && item.profile?.role && (
                            <span className="flex items-center gap-1"><Target size={10} /> {item.profile.role}</span>
                        )}
                        {isUser && item.grind_score && (
                            <span className="text-neon-cyan">GS: {item.grind_score}</span>
                        )}
                        {!isUser && item.roster && (
                            <span><Users size={10} className="inline" /> {Object.keys(item.roster).length} members</span>
                        )}
                    </div>
                </div>
                {/* Action Button */}
                <button
                    onClick={() => isUser ? onSendFriendRequest(item) : onSendTeamRequest(item)}
                    disabled={isPending}
                    className={`p-2 rounded-full transition flex items-center gap-2 px-3 text-xs font-bold
                        ${isPending
                            ? 'bg-yellow-500/10 text-yellow-500 cursor-default'
                            : 'bg-gray-700 hover:bg-neon-cyan hover:text-black text-white'}`}
                >
                    {isPending ? (
                        <>
                            <div className="w-3 h-3 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                            Pending
                        </>
                    ) : (
                        isUser ? <UserPlus size={16} /> : <Send size={16} />
                    )}
                </button>
            </div>
        );
    };

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Search Bar */}
            <div className="p-4 bg-gray-800 border-b border-gray-700">
                <div className="flex gap-2 mb-3">
                    <button
                        onClick={() => setSearchType('users')}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${searchType === 'users' ? 'bg-neon-cyan text-black' : 'bg-gray-700 text-gray-400'}`}
                    >
                        <User size={14} className="inline mr-1" /> Players
                    </button>
                    <button
                        onClick={() => setSearchType('teams')}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${searchType === 'teams' ? 'bg-neon-cyan text-black' : 'bg-gray-700 text-gray-400'}`}
                    >
                        <Shield size={14} className="inline mr-1" /> Teams
                    </button>
                </div>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder={searchType === 'users' ? 'Search players by name...' : 'Search teams by name...'}
                        className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-neon-cyan"
                    />
                    <button onClick={handleSearch} className="p-2 bg-neon-cyan text-black rounded-lg hover:bg-cyan-400 transition">
                        <Search size={18} />
                    </button>
                </div>
            </div>

            {/* Results / Suggestions */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-20">
                {loading && <p className="text-center text-gray-500 text-sm">Searching...</p>}

                {/* Show search results if available */}
                {!loading && results.length > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Search Results</h3>
                        {results.map(renderCard)}
                    </div>
                )}

                {/* Show "no results" only after search */}
                {!loading && results.length === 0 && searchQuery && (
                    <p className="text-center text-gray-500 text-sm py-4">No results found for "{searchQuery}"</p>
                )}

                {/* Show suggestions when no search is active */}
                {!searchQuery && (
                    <>
                        {loadingSuggestions && (
                            <div className="flex items-center justify-center py-8">
                                <div className="w-6 h-6 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}

                        {!loadingSuggestions && searchType === 'users' && (
                            <div className="space-y-3">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                                    <User size={12} /> Suggested Players
                                </h3>
                                {suggestedPlayers.length > 0 ? (
                                    suggestedPlayers.map(renderCard)
                                ) : (
                                    <div className="text-center py-8">
                                        <Users size={40} className="mx-auto text-gray-700 mb-3" />
                                        <p className="text-gray-500 text-sm">No players available yet.</p>
                                        <p className="text-gray-600 text-xs mt-1">Be the first to invite your teammates!</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {!loadingSuggestions && searchType === 'teams' && (
                            <div className="space-y-3">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                                    <Shield size={12} /> Featured Teams
                                </h3>
                                {featuredTeams.length > 0 ? (
                                    featuredTeams.map(renderCard)
                                ) : (
                                    <div className="text-center py-8">
                                        <Shield size={40} className="mx-auto text-gray-700 mb-3" />
                                        <p className="text-gray-500 text-sm">No teams created yet.</p>
                                        <p className="text-gray-600 text-xs mt-1">Go to Teams tab to create your own!</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DiscoverView;
